// Archivos — every file ever shared in this conversation (ChannelFilesSheet.swift, shared/API.md
// §12.5): the file-search endpoint with `in:<channel-name> `, newest first, filterable to images /
// documents, paginated by scroll with a dedupe by file id (the server may ignore pagination and
// answer everything each time). A row opens the file in the browser (images and PDFs in a tab,
// anything else downloads); "Ver mensaje" jumps to the message that carried it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { AttachmentKind } from '../../../core/attachmentKind';
import { RelativeTime } from '../../../core/relativeTime';
import type { ChannelRecord } from '../../../core/records';
import type { ChannelFileRow } from '../../../data/features/channelPanel';
import { t } from '../../../i18n/i18n';
import { useMinuteClock } from '../../hooks';
import { Button, EmptyState, InlineStatus } from '../../kit/Kit';
import { mediaUrls } from '../../media';
import { useShell } from '../../shell/ShellContext';
import { openLightbox } from '../attachments/Lightbox';
import { openPdf } from '../attachments/PostFiles';
import { VoiceRowButton } from '../voice/VoiceNoteCard';

const PAGE_SIZE = 60;
type Filter = 'all' | 'images' | 'documents';

function isImage(f: ChannelFileRow): boolean {
  return AttachmentKind.classify(f.mimeType, f.name, f.width, f.height) === 'image';
}

/** Audio plays in place, through the app's one voice player (features/voice). */
function isVoice(f: ChannelFileRow): boolean {
  return AttachmentKind.classify(f.mimeType, f.name, f.width, f.height) === 'voice';
}

/** What the browser can show by itself; everything else is a download. */
function opensInline(f: ChannelFileRow): boolean {
  return isImage(f) || f.mimeType === 'application/pdf';
}

function badge(f: ChannelFileRow): string {
  const ext = (f.extension || f.name.split('.').pop() || '').toUpperCase();
  return ext && ext !== f.name.toUpperCase() ? ext.slice(0, 4) : 'FILE';
}

export function FilesTab({ channel }: { channel: ChannelRecord }) {
  const shell = useShell();
  const now = useMinuteClock();
  const teamId = channel.teamId || shell.team?.id || '';
  const [rows, setRows] = useState<ChannelFileRow[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [state, setState] = useState({ nextPage: 0, hasMore: true, loading: false, failed: false });
  const seen = useRef(new Set<string>());
  const busy = useRef(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const loadNext = useCallback(() => {
    if (busy.current) return;
    busy.current = true;
    setState((s) => ({ ...s, loading: true, failed: false }));
    const page = state.nextPage;
    data.call('searchChannelFiles', channel.id, teamId, page, PAGE_SIZE).then(
      (answer) => {
        busy.current = false;
        if (!answer) {
          setState((s) => ({ ...s, loading: false, failed: true }));
          return;
        }
        const fresh = answer.files.filter((f) => !seen.current.has(f.fileId));
        for (const f of fresh) seen.current.add(f.fileId);
        setRows((r) => [...r, ...fresh]);
        // A short page ends it; so does a page of pure duplicates (§12.5).
        setState({ nextPage: page + 1, hasMore: answer.returned >= PAGE_SIZE && fresh.length > 0, loading: false, failed: false });
      },
      () => {
        busy.current = false;
        setState((s) => ({ ...s, loading: false, failed: true }));
      },
    );
  }, [channel.id, teamId, state.nextPage]);

  // First page on open.
  useEffect(() => {
    loadNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The next page when the end of the list scrolls into view.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !state.hasMore || state.failed) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && loadNext(), { root: el.closest('.side-panel-body'), rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [loadNext, state.hasMore, state.failed, rows.length]);

  const visible = useMemo(() => (filter === 'all' ? rows : rows.filter((f) => (filter === 'images' ? isImage(f) : !isImage(f)))), [rows, filter]);
  const filters: Array<[Filter, string]> = [
    ['all', t('channel.files.filter.all')],
    ['images', t('channel.files.filter.images')],
    ['documents', t('channel.files.filter.documents')],
  ];

  const caption = (f: ChannelFileRow) =>
    [AttachmentKind.sizeLabel(f.size), RelativeTime.format(f.createAt, now, { yesterday: t('channelList.time.yesterday') }), f.username ? '@' + f.username : '']
      .filter(Boolean)
      .join(' · ');

  let body;
  if (state.failed && !rows.length) {
    body = <InlineStatus kind="error" text={t('channel.files.error')} onRetry={loadNext} />;
  } else if (!rows.length && (state.loading || state.hasMore)) {
    body = <InlineStatus kind="loading" text={t('channel.loading.accessibility')} />;
  } else if (!visible.length) {
    body = <EmptyState title={t('channel.files.empty')} />;
  } else {
    body = (
      <div className="cp-files">
        {visible.map((f) => (
          <div key={f.fileId} className="cp-file">
            {isVoice(f) ? (
              <span className="voice-row-slot">
                <VoiceRowButton file={{ fileId: f.fileId, name: f.name, mimeType: f.mimeType }} />
              </span>
            ) : null}
            <a
              className="cp-file-main"
              href={mediaUrls.file(f.fileId, !opensInline(f))}
              target="_blank"
              rel="noopener noreferrer"
              title={t('web.channelPanel.openFile')}
              onClick={(e) => {
                // Pictures open the viewer (through every picture listed); PDFs the browser's viewer.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0 || !opensInline(f)) return;
                e.preventDefault();
                if (!isImage(f)) return openPdf(f.fileId, f.name);
                const images = visible.filter(isImage);
                openLightbox(
                  images.map((x) => ({ fileId: x.fileId, name: x.name, author: x.username ? '@' + x.username : '', at: x.createAt, width: x.width, height: x.height, mimeType: x.mimeType })),
                  images.indexOf(f),
                );
              }}
            >
              {isImage(f) ? (
                <span className="cp-thumb">
                  <img src={mediaUrls.thumbnail(f.fileId)} alt="" loading="lazy" decoding="async" />
                </span>
              ) : isVoice(f) ? null : (
                <span className="cp-thumb cp-thumb-doc">{badge(f)}</span>
              )}
              <span className="cp-file-text">
                <span className="cp-file-name">{f.name}</span>
                <span className="cp-file-caption">{caption(f)}</span>
              </span>
            </a>
            {f.postId ? (
              <Button kind="quiet" onClick={() => shell.jumpToPost(f.postId)}>
                {t('channel.files.viewMessage')}
              </Button>
            ) : null}
          </div>
        ))}
        {state.failed ? <InlineStatus kind="error" text={t('channel.files.error')} onRetry={loadNext} /> : null}
        {state.loading ? <InlineStatus kind="loading" text={t('web.channelPanel.loadingMore')} /> : null}
      </div>
    );
  }

  return (
    <>
      <div className="cp-filter" role="radiogroup" aria-label={t('channel.files.title')}>
        {filters.map(([value, label]) => (
          <button key={value} role="radio" aria-checked={filter === value} className={`cp-filter-chip${filter === value ? ' on' : ''}`} onClick={() => setFilter(value)}>
            {label}
          </button>
        ))}
      </div>
      {body}
      <div ref={sentinel} className="cp-sentinel" aria-hidden />
    </>
  );
}
